import RegionTreeSelector from "@components/Region/RegionTreeSelector";
import {BookingStep} from "@page-containers/booking/utils/bookingStep";
import {PracticeApptReason} from "@services/monolith/appointmentReasons";
import {useTranslation} from "ni18n";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTypedSelector} from "src/store";
import {selectSelectedRegion} from "src/store/slices/userLocation";
import {RootStateLocation} from "src/store/types";
import useMediaQuery from "src/useMediaQuery";
import {v4} from "uuid";

import {
  getVirtualSpecialtyIds,
  useSoonestVirtualSlotInState,
} from "../../../hooks/useSoonestVirtualSlot";
import ListPrefix from "../../../page-containers/get-care/components/ListPrefix";
import {CustomCategory, EventType} from "../../../utils/analytics/constants";
import {queueEvent} from "../../../utils/analytics/eventQueue";
import {compact} from "../../../utils/arrays";
import LocationList from "./List";
import {useListBoundaryController} from "./useClinicListBoundaryController";
import {usePairLocsWithSlotsQuery} from "./usePairLocsWithSlots";

type Props = {
  locations: RootStateLocation[];
  apptReason: PracticeApptReason;
};

type ClinicOption = {
  locationId: string | null;
  milesAway: number | null;
  soonestSlotTime: number | null;
  rank: number;
  specialtyId: string | null;
};

const sendClinicOptionsViewedEvent = (
  viewId: string,
  apptReasonId: string,
  selectedRegion: string,
  options: ClinicOption[],
) => {
  queueEvent({
    typeId: EventType.ClinicOptionsViewed,
    extraData: {
      viewId,
      category: CustomCategory.AppointmentScheduling,
      bookingStep: BookingStep.LOCATION_SELECTION,
      apptDetails: {apptReasonId},
      selectedRegion,
      options,
    },
  });
};

const ClinicList: React.FC<Props> = React.memo(({locations, apptReason}) => {
  const i18n = useTranslation();

  const [viewId, setViewId] = useState<string>();

  const locationsSorted = useTypedSelector(state => state.config.locationsSorted);
  const selectedRegion = useTypedSelector(selectSelectedRegion);

  const isSm = useMediaQuery("sm");

  const locsInRegion = useMemo(
    () => locations.filter(l => l.region.slug === selectedRegion),
    [locations, selectedRegion],
  );

  const locSlotsQuery = usePairLocsWithSlotsQuery({
    locations: locsInRegion,
    apptReason,
    skip: !locationsSorted,
  });

  const {data: locsToRender} = locSlotsQuery;
  const reasonsVirtualSpecialtyIds = useMemo(
    () => getVirtualSpecialtyIds(apptReason),
    [apptReason],
  );

  const soonestVirtualSlotQuery = useSoonestVirtualSlotInState(
    reasonsVirtualSpecialtyIds,
    apptReason.id,
  );

  const {ListController, bindList, stop} = useListBoundaryController(
    locsToRender.length,
    isSm ? Math.min(4, locsToRender.length) : locsToRender.length,
  );

  const firstLocRef = useRef<HTMLLIElement>(null);

  const onListReset = () => {
    if (firstLocRef.current) {
      firstLocRef.current.scrollIntoView({block: "center", behavior: "smooth"});
    }
  };

  useEffect(() => {
    /**
     * On one render after the locsSlotsQuery settles, the list is not yet bound.
     * This means that the bound list length is 0, but the data is actually longer.
     * This check ensures that we don't fire the viewed event if the bound list stops
     * at 0, but the data is longer.
     */
    const listNotYetBound = stop === 0 && locSlotsQuery.data?.length > 0;
    if (locSlotsQuery.isSettled && soonestVirtualSlotQuery.isSettled && !listNotYetBound) {
      const clinicOptions = bindList(locSlotsQuery.data).map(
        ({location, soonestSlot, specialtyId}, i) => ({
          locationId: location.id,
          milesAway: location.dist || null,
          rank: i + 1,
          soonestSlotTime: soonestSlot?.time || null,
          specialtyId: specialtyId || null,
        }),
      );

      const virtualOption = soonestVirtualSlotQuery.data
        ? {
            locationId: null,
            milesAway: null,
            rank: clinicOptions.length + 1,
            soonestSlotTime: soonestVirtualSlotQuery.data?.time || null,
            specialtyId: soonestVirtualSlotQuery.data?.specialtyIds[0] || null,
          }
        : null;

      const newViewId = v4();

      sendClinicOptionsViewedEvent(
        newViewId,
        apptReason.id,
        selectedRegion,
        compact([...clinicOptions, virtualOption]),
      );

      setViewId(newViewId);
    }
    // Do not add selected region. Only refire this event when data changes and query is settled.
  }, [apptReason.id, soonestVirtualSlotQuery, locSlotsQuery, bindList, stop]);

  const shouldShowResultCount = !isSm && locsToRender.length > 0;

  return (
    <section className="ph2 ph0-sm pt6-sm" aria-labelledby="book-appointment-h2">
      <h2 id="book-appointment-h2" className="fs32 fs24-md ls-25 wsnw ph6 ph4-sm">
        {i18n.t("Book an appointment")}
      </h2>
      <div className="df aic jcsb pos-r pl6 pr12 ph4-sm" style={{marginLeft: "-.6rem"}}>
        <RegionTreeSelector />
        <ListPrefix
          text={i18n.t("Skip location list")}
          skipTargetId="insurance-and-pricing"
          resultCount={locsToRender.length}
          shouldShowResultCount={shouldShowResultCount}
        />
      </div>
      <LocationList
        loading={!locSlotsQuery.isSettled}
        apptReason={apptReason}
        locations={bindList(locSlotsQuery.data)}
        firstLocRef={firstLocRef}
        isSm={isSm}
        // @ts-expect-error TS2322: Type 'QueryState<ApptSlot | null>' is not assignable to type 'QueryState<ApptSlot>'.
        soonestVirtualSlotQuery={soonestVirtualSlotQuery}
        viewId={viewId}
      />

      {isSm && locsToRender.length > 4 && (
        <div className="ph6 ph4-sm">
          <ListController onReset={onListReset} />
        </div>
      )}
    </section>
  );
});

ClinicList.displayName = "ClinicList";
export default React.memo(ClinicList);
